/**
 * OAuthCompletePage — landing page for the OAuth success redirect.
 *
 * Route: /auth/oauth/complete (PUBLIC — outside RequireAuth, since the user
 * is mid-authentication and useAuth() hasn't resolved a session yet).
 *
 * Backend contract: on a successful social sign-in the backend
 * 302-redirects the browser here with the freshly minted JWT in the URL
 * **fragment** (location.hash), NOT the query string:
 *
 *     /auth/oauth/complete#token=<JWT>
 *
 * The fragment is never sent to any server, so the token doesn't leak into
 * access logs / referrers — we read it client-side and persist it.
 *
 * This page serves BOTH OAuth contexts:
 *
 *   • POPUP (window.opener present, opened by useOAuthSignIn): this page is a
 *     short-lived popup. It must NOT persist/navigate itself — instead it
 *     relays the result to the opener via postMessage and closes. On a token →
 *     postMessage({ type: 'gdb-oauth-token', token }); on a missing token →
 *     postMessage({ type: 'gdb-oauth-error', reason: 'missing_token' }). The
 *     opener does the persist + navigate. targetOrigin is always
 *     window.location.origin (never '*').
 *
 *   • INLINE / embed (no opener — full-page redirect, e.g. WebView2 where the
 *     popup was blocked and signIn fell back to window.location.href): KEEP the
 *     existing behavior exactly:
 *       1. Parse `location.hash` for `token`.
 *       2. If present → persist it via persistOAuthToken (the SAME localStorage
 *          'gdb_token' write + invalidateAuth the popup opener uses), then
 *          navigate to /account.
 *       3. If absent → navigate to /login?oauth_error=missing_token. (Other
 *          failures are handled by the backend, which redirects straight to
 *          /login?oauth_error=<reason> and never reaches this page.)
 *
 * Runs once (useRef guard) so React StrictMode's double-effect doesn't
 * double-navigate / double-post.
 */

import { useEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQueryClient } from '@tanstack/react-query'
import { Loader2 } from 'lucide-react'
import {
  persistOAuthToken,
  OAUTH_TOKEN_MESSAGE,
  OAUTH_ERROR_MESSAGE,
} from '@/lib/auth'

/**
 * Extract the `token` value from a URL fragment like `#token=<jwt>` (or
 * `#token=<jwt>&foo=bar`). Returns null when absent or empty.
 */
function parseTokenFromHash(hash: string): string | null {
  // Strip the leading '#'. URLSearchParams handles the key=value&… parse and
  // percent-decoding for us.
  const fragment = hash.startsWith('#') ? hash.slice(1) : hash
  const token = new URLSearchParams(fragment).get('token')
  return token && token.length > 0 ? token : null
}

/** True when this page is rendered inside a popup opened by useOAuthSignIn. */
function isPopupContext(): boolean {
  return !!window.opener && window.opener !== window
}

export function OAuthCompletePage() {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const handled = useRef(false)

  useEffect(() => {
    if (handled.current) return
    handled.current = true

    const token = parseTokenFromHash(window.location.hash)

    // ----- Popup context: relay to the opener + close. -----
    if (isPopupContext()) {
      const origin = window.location.origin
      if (token) {
        console.log('[oauth] popup complete — relaying token to opener')
        window.opener.postMessage({ type: OAUTH_TOKEN_MESSAGE, token }, origin)
      } else {
        console.warn('[oauth] popup complete without a token — relaying error to opener')
        window.opener.postMessage(
          { type: OAUTH_ERROR_MESSAGE, reason: 'missing_token' },
          origin,
        )
      }
      window.close()
      return
    }

    // ----- Inline / embed context: persist + navigate (unchanged behavior). -----
    if (!token) {
      console.warn('[oauth] complete page reached without a token in the fragment')
      navigate('/login?oauth_error=missing_token', { replace: true })
      return
    }

    // Persist the JWT the same way a normal login does, then refresh auth
    // state so useAuth() re-validates the new Bearer against the sidecar.
    console.log('[oauth] received token via fragment, persisting + refreshing auth')
    void (async () => {
      await persistOAuthToken(queryClient, token)
      navigate('/account', { replace: true })
    })()
  }, [navigate, queryClient])

  return (
    <div className="flex min-h-[calc(100vh-10rem)] items-center justify-center p-4">
      <div className="flex flex-col items-center gap-3 text-ue-text-secondary">
        <Loader2 className="w-6 h-6 animate-spin text-ue-accent-primary" />
        <p className="text-sm">Completing sign-in…</p>
      </div>
    </div>
  )
}
